import { useState, useEffect } from "react";
import axios from "axios";
import { Scale, Box } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingSettingsForm = ({ settings, onSuccess }) => {
  const [ratePerLbs, setRatePerLbs] = useState(settings?.rate_per_lbs || 5);
  const [ratePerVolume, setRatePerVolume] = useState(settings?.rate_per_volume || 5);
  const [laptopRate, setLaptopRate] = useState(settings?.laptop_rate || 60);
  const [perfumeRate, setPerfumeRate] = useState(settings?.perfume_rate || 20);
  const [telephoneRate, setTelephoneRate] = useState(settings?.telephone_rate || 27);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (settings) {
      setRatePerLbs(settings.rate_per_lbs || 5);
      setRatePerVolume(settings.rate_per_volume || 5);
      setLaptopRate(settings.laptop_rate || 60);
      setPerfumeRate(settings.perfume_rate || 20);
      setTelephoneRate(settings.telephone_rate || 27);
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.put(
        `${API}/settings/pricing`,
        { 
          rate_per_lbs: parseFloat(ratePerLbs), 
          rate_per_volume: parseFloat(ratePerVolume),
          laptop_rate: parseFloat(laptopRate),
          perfume_rate: parseFloat(perfumeRate),
          telephone_rate: parseFloat(telephoneRate)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pricing updated successfully!");
      onSuccess(res.data.settings);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pricing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border-b border-slate-200 pb-4 mb-4">
        <h4 className="font-semibold text-slate-700 mb-3">Custom Rates</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
              <Scale className="w-4 h-4" /> Rate per lbs (USD)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={ratePerLbs}
              onChange={(e) => setRatePerLbs(e.target.value)}
              className="h-12 text-lg font-mono"
              data-testid="rate-per-lbs"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
              <Box className="w-4 h-4" /> Rate per ft³ (USD)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={ratePerVolume}
              onChange={(e) => setRatePerVolume(e.target.value)}
              className="h-12 text-lg font-mono"
              data-testid="rate-per-volume"
            />
          </div>
        </div>
      </div>
      
      <div>
        <h4 className="font-semibold text-slate-700 mb-3">Fixed Rates</h4>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Label className="text-sm w-24">Laptop</Label>
            <div className="flex items-center flex-1">
              <span className="text-slate-400 mr-2">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={laptopRate}
                onChange={(e) => setLaptopRate(e.target.value)}
                className="h-10 font-mono"
                data-testid="laptop-rate"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm w-24">Perfume</Label>
            <div className="flex items-center flex-1">
              <span className="text-slate-400 mr-2">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={perfumeRate}
                onChange={(e) => setPerfumeRate(e.target.value)}
                className="h-10 font-mono"
                data-testid="perfume-rate"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm w-24">Telephone</Label>
            <div className="flex items-center flex-1">
              <span className="text-slate-400 mr-2">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={telephoneRate}
                onChange={(e) => setTelephoneRate(e.target.value)}
                className="h-10 font-mono"
                data-testid="telephone-rate"
              />
            </div>
          </div>
        </div>
      </div>
      
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
        data-testid="save-pricing-btn"
      >
        {loading ? "Saving..." : "Save Pricing"}
      </Button>
    </form>
  );
};

export default PricingSettingsForm;
